/**
 * Formatting, the URL param read and the note style. Dates and sums come from the feed (G1); the client
 * places them on a ruler and prints them (spec.json do_not_build).
 */
import {
  KIND_BOILER_INSPECT,
  KIND_BOILER_REINSPECT,
  KIND_BOILER_REPORT_MISSED,
  KIND_DEP_REGISTRATION,
  KIND_ELEVATOR_BRAKE,
  KIND_GAS_ALARMS,
  type ActionItem,
  type ActionPlanFeed,
} from "@/components/portal/useActionPlanFeed";
import {
  BOILER_LOW,
  DEP_BOILER_REGISTRATION,
  ELEVATOR_BRAKE,
  GAS_ALARMS,
  suggestedActionsForCode,
} from "@/components/portal/lawSteps";
import { CASE_STAGE_LABELS } from "@/components/portal/caseStageLabels";
import { nyDate } from "@/components/portal/violationRemediation/matter";
import type { CaseVariant } from "@/queries/violationCasesQueries";
import {
  DAY_MS,
  daysBetween,
  fmt,
  formatActBy,
  formatDay,
  utc,
} from "@/components/portal/shortDay";

export { daysBetween, formatActBy, formatDay };

export const shiftIso = (iso: string, days: number): string =>
  new Date(utc(iso) + days * DAY_MS).toISOString().slice(0, 10);

/** 0 Sunday to 6 Saturday. */
export const weekday = (iso: string): number => new Date(utc(iso)).getUTCDay();

/** Ruler token: "OCT 16". */
export const formatTick = (iso: string): string =>
  fmt(iso, { month: "short", day: "numeric" }).toUpperCase();

/** Axis month: "Oct". */
export const formatMonth = (iso: string): string =>
  fmt(iso, { month: "short" });

/** An instant's New York day and clock: { day: "2026-10-31", time: "9:00 AM" }. */
export const nyClock = (at: string): { day: string; time: string } => {
  const d = new Date(at);
  const time = d.toLocaleTimeString("en-US", {
    timeZone: "America/New_York",
    hour: "numeric",
    minute: "2-digit",
  });
  return { day: nyDate(d), time };
};

/** "Wed Oct 7, 9:45 AM" in New York. */
export const sinceLine = (at: string, today: string): string => {
  const { day, time } = nyClock(at);
  return `${formatDay(day, today)}, ${time}`;
};

/** `deadlines.law_date_line` (Leo 10-11): "DOB's date is Sat Oct 31.", "" while `shown` is the agency's own
 * date. Parity: backend `tests/fixtures/law_date_lines.json`. Prefer the wire's `actByNote`/`deadlineNote`. */
export const lawDateLine = (
  legal: string,
  shown: string,
  today: string,
  who = "DOB",
): string =>
  shown === legal
    ? ""
    : `${who}'s date is ${formatDay(legal, today)}${[0, 6].includes(weekday(legal)) ? "." : ", a holiday."}`;

/** The Action plan tooltip's face: ColHits and the LL33 rail's Tip. */
export const AP_TIP =
  "motion-pop max-w-[300px] border-dc-line-strong bg-dc-paper text-[12px] leading-[17px] text-dc-ink shadow-dc-lift";

/** A date as formatDay, formatActBy and formatRecordDate print it ("Sat Oct 31", "Oct 8, 2025"); one group, for split. */
export const SHOWN_DATE =
  /((?:(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun) )?(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) \d{1,2}(?:, \d{4})?)/;

/** "Sep 18" this year, "Jan 31, 2025" otherwise. */
export const formatRecordDate = (iso: string, today: string): string =>
  iso.slice(0, 4) === today.slice(0, 4)
    ? fmt(iso, { month: "short", day: "numeric" })
    : fmt(iso, { month: "short", day: "numeric", year: "numeric" });

/** `as_of` is a UTC instant; the reader's day is New York's. */
export const formatAsOf = (asOf: string | null): string | null => {
  if (!asOf) return null;
  const d = new Date(asOf);
  if (Number.isNaN(d.getTime())) return null;
  return `Read ${d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "America/New_York",
  })}`;
};

/** "FDNY 012182839Z", "DOB 2 judgments", "FDNY 3 · DOB 1". */
export const recordLine = (item: ActionItem): string => {
  if (item.kind === "proof") {
    return Object.entries(item.agencyCounts)
      .map(([a, n]) => `${a === "UNKNOWN" ? "Other" : a} ${n}`)
      .join(" · ");
  }
  const noun =
    item.kind === "pay" ? "judgments" : `${item.recordNoun || "ticket"}s`;
  return item.groupedCount > 1
    ? `${item.agency} ${item.groupedCount} ${noun}`
    : `${item.agency} ${item.recordIds[0] ?? ""}`.trim();
};

/** FetchNotes and the Ontology's paths alert. */
export const NOTE_CLASS =
  "border-l-[3px] border-dc-risk-med bg-dc-risk-med-soft px-3 py-1.5 text-dc-ink";

/** A lane or pick row with no dated work (Leo 10-14: "more concise"); the deck and the dock read it. */
export const NOTHING_DATED = "Nothing dated";
/** A dock block row the pick emptied. */
export const NOTHING_ELSE_DATED = "Nothing else dated";
/** The dock's pick row when every row of the pick is past due (Leo 10-14). */
export const ALL_PAST_DUE = "All past due";

/** The rail's id: the stack and a phone pick scroll to it. */
export const CX_ID = "cx";

export const readParam = (search: unknown, k: string): string | undefined => {
  const v = (search as Record<string, unknown> | undefined)?.[k];
  if (typeof v === "number") return String(v);
  return typeof v === "string" && v ? v : undefined;
};

export const allItems = (feed: ActionPlanFeed): ActionItem[] => [
  ...feed.items,
  ...feed.later,
];

/** Composite slot names the feed reports as not read. */
const SOURCE_NAMES: Record<string, string> = {
  ecb: "ECB",
  oath_dob: "OATH",
  oath_other_agency: "OATH, other agencies",
  boiler: "Boilers",
  dob_now: "DOB NOW",
  dob_inspector: "DOB inspections",
};
export const sourceName = (slot: string): string => SOURCE_NAMES[slot] ?? slot;

export type StepFork = { title: string; then: string }[];
export interface StepsPlan {
  variant: CaseVariant | null;
  stage: string | null;
  steps: (string | StepFork)[];
  end: string;
  /** The item's own step, marked Next; the ones before it are done. Default 0. */
  at?: number;
}

const REOPEN_FORK = (reason: boolean): StepFork => [
  {
    title: "Ask OATH for a new hearing",
    then: reason ? "Needs a reason. If refused, pay" : "Returns as a hearing",
  },
  { title: "Pay", then: "Ends the new-hearing option" },
];

/** Plain steps per `steps_id`. Stage names are the counsel-pending placeholders. */
export const stepsFor = (item: ActionItem): StepsPlan => {
  const vendor = item.needs.includes("VENDOR")
    ? ["A vendor fixes the cause"]
    : [];
  switch (item.stepsId) {
    case "reopen":
    case "reason":
      return {
        variant: "reopen",
        stage: "defaulted",
        steps: [REOPEN_FORK(item.stepsId === "reason"), ...vendor],
        end: "Closes: dismissed or paid",
      };
    case "hearing":
    case "new_hearing":
      return {
        variant: "contest",
        stage: "hearing",
        steps: [...vendor, "Answer at OATH, or hand it to CodeClear"],
        end: "Closes: dismissed or decided",
      };
    case "check_result":
      return {
        variant: "contest",
        stage: "decision",
        steps: [
          "Look up the hearing result",
          "If it was a default, ask for a new hearing",
        ],
        end: "Closes: result on record",
      };
    case "appeal":
      return {
        variant: "appeal",
        stage: "adjudicated",
        steps: ["Get advice on an appeal", "Pay if you do not appeal"],
        end: "Closes: paid or reversed",
      };
    case "pay":
      return {
        variant: null,
        stage: null,
        steps: ["Pay the judgment"],
        end: "Closes: paid",
      };
    case "proof":
      return {
        variant: "cure_certify",
        stage: "correction",
        steps: ["Send the agency proof of the correction"],
        end: "Closes: proof accepted",
      };
    case "dob_certify":
    case "fdny_certify":
      return {
        variant: "cure_certify",
        stage: "correction",
        steps: [
          "Fix the condition",
          `Certify the correction with ${item.kind === "fdny_certify" ? "FDNY" : "DOB"}`,
        ],
        end: "Closes: certificate accepted",
      };
    // LL84 branches: no feed row until law rows (plan_final §11.3).
    case "ll84_file":
      return {
        variant: null,
        stage: null,
        steps: [
          "File the benchmarking report",
          "DOB issues a new violation each quarter it stays unfiled",
        ],
        end: "Closes: report accepted",
      };
    case "ll84_data":
      return {
        variant: null,
        stage: null,
        steps: ["Fix the LL84 data and resubmit"],
        end: "Closes: report accepted",
      };
    case "boiler_report":
      return {
        variant: null,
        stage: null,
        steps: ["File the boiler inspection report"],
        end: "Closes: report accepted",
      };
    // Boiler and DEP rows read the Codes tab's steps (one wording per flow).
    case KIND_BOILER_INSPECT:
    case KIND_BOILER_REINSPECT:
      return {
        variant: null,
        stage: null,
        steps: suggestedActionsForCode(BOILER_LOW),
        end: "Closes: report accepted",
        at: item.stepsId === KIND_BOILER_REINSPECT ? 1 : 0,
      };
    // The missed cycle is closed: its bill first, then this year's inspection.
    case KIND_BOILER_REPORT_MISSED:
      return {
        variant: null,
        stage: null,
        steps: [
          "Pay DOB's bill for the missed report when it comes",
          suggestedActionsForCode(BOILER_LOW)[0],
        ],
        end: "Closes: bill paid",
      };
    case KIND_DEP_REGISTRATION:
      return {
        variant: null,
        stage: null,
        steps: suggestedActionsForCode(DEP_BOILER_REGISTRATION),
        end: "Closes: renewal accepted",
      };
    case KIND_ELEVATOR_BRAKE:
      return {
        variant: null,
        stage: null,
        steps: suggestedActionsForCode(ELEVATOR_BRAKE),
        end: "Closes: permit signed off",
      };
    case KIND_GAS_ALARMS:
      return {
        variant: null,
        stage: null,
        steps: suggestedActionsForCode(GAS_ALARMS),
        end: "Closes: alarms in",
      };
    default:
      return { variant: null, stage: null, steps: [], end: "" };
  }
};

export const stageLabels = (variant: CaseVariant): [string, string][] =>
  Object.entries(CASE_STAGE_LABELS[variant]);
